'use client';

import { useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';

import { useAccess } from '@/src/access/access-context';
import {
  getAuthNavigationHoldSnapshot,
  subscribeAuthNavigationHold,
} from '@/src/auth/auth-navigation-hold';

import { ContactCenterRealtimeProvider } from '../realtime/contact-center-realtime-provider';
import { canUseVoiceRuntime } from './voice-permissions';
import { VoiceWorkspaceProvider } from './voice-workspace-provider';

export function VoiceRootBoundary({ children }: { children: ReactNode }) {
  const { access, activeTenant, user } = useAccess();
  const navigationHeld = useSyncExternalStore(
    subscribeAuthNavigationHold,
    getAuthNavigationHoldSnapshot,
    () => false,
  );
  const authorizedLifetime = useMemo(() => activeTenant && access ? {
    tenantId: activeTenant.id,
    authorityKey: `${activeTenant.id}:${user?.id ?? 'unknown'}`,
    canUseContactCenterRealtime: Boolean(
      access.effectivePermissions.some((permission) =>
        !permission.blockedByDisabledModule &&
        (permission.moduleKey === 'contact_center' || permission.key.startsWith('organization.voice.')),
      ),
    ),
    canUseVoiceRuntime: canUseVoiceRuntime(access),
  } : null, [access, activeTenant, user?.id]);
  const authorizedLifetimeKey = authorizedLifetime
    ? `${authorizedLifetime.authorityKey}:${authorizedLifetime.canUseContactCenterRealtime}:${authorizedLifetime.canUseVoiceRuntime}`
    : 'none';
  const [retainedState, setRetainedState] = useState({
    key: authorizedLifetimeKey,
    lifetime: authorizedLifetime,
  });

  if (!navigationHeld && retainedState.key !== authorizedLifetimeKey) {
    setRetainedState({
      key: authorizedLifetimeKey,
      lifetime: authorizedLifetime,
    });
  }

  const lifetime = navigationHeld
    ? retainedState.lifetime
    : authorizedLifetime;

  if (!lifetime?.canUseContactCenterRealtime) return <>{children}</>;

  const registrationAuthorized = Boolean(
    !navigationHeld &&
    authorizedLifetime?.tenantId === lifetime.tenantId &&
    authorizedLifetime.canUseVoiceRuntime,
  );

  return (
    <ContactCenterRealtimeProvider
      key={lifetime.authorityKey}
      persistentBrowserVoice={registrationAuthorized}
    >
      {lifetime.canUseVoiceRuntime ? <VoiceWorkspaceProvider>{children}</VoiceWorkspaceProvider> : children}
    </ContactCenterRealtimeProvider>
  );
}
